/**
 * Common JS controller.
 *
 * Use this class to run scripts globally and to provide
 * modular helper functions for other scripts classes.
 */

// Use jQuery as $ within this file scope.
const $ = jQuery;

// Require fastclick for a faster mobile UX.
import FastClick from 'fastclick';

/**
 * Class Common
 */
class Common {

    /**
     * Class constructor is for binding class properties.
     */
    constructor() {
    }

    /**
     * Cache dom elements for use in the class's methods
     */
    cache() {

        // The accessible outline style container.
        this.focusStyleContainer = document.getElementById( 'accessible-outline' );

        // Scan all object-fit-containers on the page.
        this.objectfitContainers = $( '.js-object-fit-image-container' );

        // Assign back to top-element to variable
        this.scrollButton = document.getElementById( 'js-back-to-top' );

        this.galleries = $( '.js-image-gallery' );

        // Bid link
        this.machineBidLink = $( '.machine__links__btn--bid' );

    }

    /**
     * All common events
     * @return {void}
     */
    events() {

        // Add the outline nullifying style to the
        // container in the document head when using the mouse.
        document.body.addEventListener( 'mousedown', e => {
            this.nullifyOutline();
        });

        // Add the outline nullifying style to the
        // container in the document head on touchevents.
        document.body.addEventListener( 'touchstart', e => {
            this.nullifyOutline();
        });

        // Remove the outline nullifying style from the
        // container in the document head when using the keyboard.
        document.body.addEventListener( 'keydown', e => {
            if ( this.focusStyleContainer ) {
                this.focusStyleContainer.innerHTML = '';
            }
        });

        // Add click listener to bid links
        this.machineBidLink.on( 'click', ( e ) => {
            this.saveMachineInfo( e );
        });
    }

    /**
     * Init all found galleries.
     */
    initGalleries() {

        // This inits all found galleries with the lightGallery JS plugin.
        if ( this.galleries.length > 0 ) {
            this.galleries.lightGallery();
        }

    }

    /**
     * Add nullifying outline CSS rules to focusStyleContainer.
     */
    nullifyOutline() {
        if ( this.focusStyleContainer ) {
            this.focusStyleContainer.innerHTML = '*:focus{outline:none!important;}';
        }
    }

    /**
     * Fallback for object fit.
     */
    objectFitFallback() {

        // IE and Edge CSS Object fit fallback javascript
        if ( ! Modernizr.objectfit ) {

            // Loop through object fit containers.
            this.objectfitContainers.each( function() {

                // Variables
                const $container = $( this );
                const imgUrl     = $container.find( 'img' ).prop( 'src' );

                if ( imgUrl ) {
                    $container
                    .css( 'backgroundImage', 'url(' + imgUrl + ')' )
                    .addClass( 'compat-object-fit' );
                }
            });
        } // End if().
    }

    /**
     * Scroll back to the top of the page
     */
    backToTop() {

        // Top of the document in pixels
        const documentTop = 0;

        // This is the amount in pixels that user needs to scroll before back to top-button appears
        const distanceFromTop = 500;

        // When the user scrolls down from the top of the document, show the button
        $( window ).scroll( () => {
            if ( document.body.scrollTop > distanceFromTop || document.documentElement.scrollTop > distanceFromTop ) {
                this.scrollButton.classList.remove( 'js-hidden' );
            } else {
                this.scrollButton.classList.add( 'js-hidden' );
            }
        });

        // When the user clicks on the button, scroll to the top of the document
        this.scrollButton.onclick = function() {
            document.body.scrollTop = documentTop;
            document.documentElement.scrollTop = documentTop;
        };

    }

    /**
     * Save the machine info to local storage for form pre-filing
     * @param {event} event Click event.
     */
    saveMachineInfo( event ) {

        event.preventDefault();

        const $machine = $( event.target ).closest( '.machine' );

        // Combine machine brand and model, and trim extra spaces
        const machineInfo = ( $machine.find( '.machine__info__header__brand' ).text() + $machine.find( '.machine__info__header__model' ).text() ).replace( /\s+/g, ' ' ).trim();

        // Set the selected machine information to session storage
        sessionStorage.setItem(
            'rotator_selected_machine',
            machineInfo
        );

        // Follow the link
        window.location = $( event.target ).attr( 'href' );
    }

    /**
     * Run when the document is ready.
     */
    docReady() {

        this.cache();
        this.events();
        FastClick.attach( document.body );
        this.objectFitFallback();
        this.backToTop();
        this.initGalleries();
    }

    /**
     * Toggles the state of an elements aria-hidden attribute. If the element does not have an aria-hidden attribute,
     * one will be created and set to false.
     *
     * @param {object} elements The nodelist containing the elements
     * @return {void}
     */
    static toggleAriaHidden( element ) {
        const ariaExpandedState = element.getAttribute( 'aria-hidden' ) === 'false' ? true : false;
        element.setAttribute( 'aria-hidden', ariaExpandedState );
    }

    /**
     * Removes classname from each element in a nodelist or array of elements.
     *
     * @param {object} elements  The nodelist containing the elements
     * @param {string} className The classlist to remove
     * @return {void}
     */
    static removeClassFromAll( elements, className ) {

        // Return early if missing required attributes.
        if ( ! elements || ! className  ) {
            return;
        }

        Array.from( elements ).forEach( element => {
            element.classList.remove( className );
        });
    }

    /**
     * Set attribute on each element in a nodelist or array of elements.
     *
     * @param {object} elements  The nodelist or array containing the elements
     * @param {string} attribute The attribut to change
     * @param {string} value     The value of the attribute
     * @return {void}
     */
    static setAttrOnAll( elements, attribute, value ) {

        // // Return early if missing required attributes.
        if ( ! elements || ! attribute || ! value  ) {
            return;
        }

        Array.from( elements ).forEach( element => {
            element.setAttribute( attribute, value );
        });
    }

    /**
     * Get all the siblings of an element. Optionally specify a classname to only get the elements with said class.
     *
     * @param {object} element   The element whose sibilings to find.
     * @param {string} classname Optional classname that siblings must match.
     * @return {object}    Returns empty array if no matches are found; otherwise, it returns all matching elements.
     */
    static siblings( element, classname ) {

        // Return empty arry if missing required attributes
         if ( ! element ) {
            return [];
        }

        return Array.prototype.filter.call( element.parentNode.children,
            function( child ) {
                if ( classname ) {
                    if ( ! child.classList.contains( classname ) ) {
                        return false;
                    }
                }
                return child !== element;
            }
        );
    }

    /**
     * Select a list of matching child elements from an array of elements.
     *
     * @param  {object} elements A nodelist. Use each item in nodelist as context when finding the children.
     * @param  {string} selector The query selector string.
     * @return {object]}   Returns empty array if no matches are found; otherwise, it returns all matching elements.
     */
    static childrenFromEach( elements, selector ) {

        // Return empty array if missing required attributes or elements empty
        if ( ! elements || ! selector || ! elements.length > 0 ) {
            return [];
        }

        const arrayOfNodes = Array.prototype.map.call( elements,
            function( element ) {

                // Get all matching elements.
                const matchingChildren = ( element ).querySelectorAll( selector );

                // Convert nodelist to array.
                return [ ...matchingChildren ];
            }
        );

        // Flatten the array
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/Reduce?v=a
        const flattened = arrayOfNodes.reduce( ( a, b ) => [ ...a, ...b ]);
        return flattened;
    }

    /**
     * Offers safe way to stop a JS event.
     *
     * Example usage:
     * Theme.Common.stop(e);
     *
     * @param e Event object.
     */
    static stop( e ) {
        e.preventDefault ? e.preventDefault() : ( e.returnValue = false );
    }

    /**
     * Select a list of matching elements, context is optional.
     *
     * @param  {string} selector The query selector string.
     * @param  {object} context  A query context object.
     * @return {object}          Returns empty nodelist if no matches are found; otherwise, it returns a nodelist of matching elements.
     */
    static $( selector, context ) {
        return ( context || document ).querySelectorAll( selector );
    }

    /**
     * Select the first match only, context is optional.
     *
     * @param  {string} selector The query selector string.
     * @param  {object} context  A query context object.
     * @return {object|null}     Returns null if no matches are found; otherwise, it returns the first matching element.
     */
    static $1( selector, context ) {
        return ( context || document ).querySelector( selector );
    }

    static getUrlParameter( name ) {
        name          = name.replace( /[\[]/, '\\[' ).replace( /[\]]/, '\\]' );
        const regex   = new RegExp( '[\\?&]' + name + '=([^&#]*)' );
        const results = regex.exec( location.search );

        return results === null ? false : decodeURIComponent( results[1].replace( /\+/g, ' ' ) );
    };

}

// Export the class reference.
module.exports = Common;



// WEBPACK FOOTER //
// ./assets/scripts/common.js